
import React from 'react';
import { UseFormReturn } from 'react-hook-form';
import { FormField, FormItem, FormLabel, FormControl, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LineItemFormValues } from './hooks/useLineItemFormSchema';
import { useUnitOptions } from './hooks/useUnitOptions';
import { useAuth } from '@/contexts/useAuth';

interface UnitCostFieldsProps {
  form: UseFormReturn<LineItemFormValues>;
}

const UnitCostFields: React.FC<UnitCostFieldsProps> = ({ form }) => {
  const { profile } = useAuth();
  const { customUnitOptions, loading } = useUnitOptions(profile?.company_id);
  // Helper function to handle numeric input and prevent decimal drift
  const handleNumericInput = (value: string, fieldName: string) => {
    if (value === '' || value === undefined) {
      form.setValue(fieldName as any, '' as any);
      return;
    }
    
    const numericValue = parseFloat(value);
    if (!isNaN(numericValue)) {
      const roundedValue = Math.round(numericValue * 100) / 100;
      form.setValue(fieldName as any, roundedValue);
    }
  };

  const handleNumericBlur = (fieldName: string) => {
    const val = form.getValues(fieldName as any);
    if (val === '' || val === undefined || val === null) {
      form.setValue(fieldName as any, 0);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <FormField
        control={form.control}
        name="base_unit"
        render={({ field }) => (
          <FormItem>
            <FormLabel htmlFor="base_unit">Base Unit *</FormLabel>
            <Select
              disabled={loading}
              value={field.value}
              onValueChange={field.onChange}
              name="base_unit"
            >
              <FormControl>
                <SelectTrigger id="base_unit">
                  <SelectValue placeholder={loading ? "Loading units..." : "Select unit"} />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                {customUnitOptions.length > 0 ? (
                  customUnitOptions.map((unit) => (
                    <SelectItem key={unit.value} value={unit.value}>
                      {unit.label} ({unit.symbol}) {unit.is_default && '(Default)'}
                    </SelectItem>
                  ))
                ) : loading ? (
                  <SelectItem value="loading" disabled>Loading units...</SelectItem>
                ) : (
                  <SelectItem value="" disabled>No units available</SelectItem>
                )}
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="base_cost"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Base Cost *</FormLabel>
            <FormControl>
              <Input
                type="number"
                step="0.01"
                min="0"
                placeholder="Enter base cost"
                {...field}
                value={field.value ?? ''}
                onChange={(e) => {
                  handleNumericInput(e.target.value, 'base_cost');
                }}
                onBlur={() => handleNumericBlur('base_cost')}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};

export default UnitCostFields;
